import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { withRealPrefix } from '../utils/siteMode';
import ProductShowcase from './ProductShowcase';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loginWithQQ, loginWithEmail, registerWithEmail } = useAuth();
  const isLoginPage = location.pathname === withRealPrefix('/login');
  
  // 登录模式：'social' 社交登录, 'email' 账号密码登录, 'register' 注册
  const [mode, setMode] = useState('social');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 已登录访问登录页时跳转社区
  useEffect(() => {
    if (isAuthenticated && isLoginPage) {
      navigate(withRealPrefix('/forum'));
    }
  }, [isAuthenticated, isLoginPage, navigate]);

  // 账号密码登录
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('请填写账号和密码');
      return;
    }

    setLoading(true);
    setError('');

    const result = await loginWithEmail(email, password);
    
    if (result.success) {
      navigate(withRealPrefix('/forum'));
    } else {
      setError(result.error || '登录失败');
    }
    
    setLoading(false);
  };

  // 邮箱注册
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('请填写邮箱和密码');
      return;
    }
    if (password !== confirmPassword) {
      setError('两次密码输入不一致');
      return;
    }
    if (password.length < 6) {
      setError('密码至少6位');
      return;
    }

    setLoading(true);
    setError('');

    const result = await registerWithEmail(email, password, nickname || email.split('@')[0]);
    
    if (result.success) {
      navigate(withRealPrefix('/forum'));
    } else {
      setError(result.error || '注册失败');
    }
    
    setLoading(false);
  };

  return (
    <div style={styles.container}>
      {/* 产品展示背景 */}
      <ProductShowcase />

      {/* 登录卡片 */}
      <div style={styles.card}>
        <div style={styles.logoSection}>
          <img 
            src={`${process.env.PUBLIC_URL}/LOGO.png`} 
            alt="logo" 
            style={styles.logo}
          />
        </div>
        
        <h1 style={styles.title}>
          {mode === 'register' ? '创建账号' : '欢迎访问蓝卡模玩社区'}
        </h1>
        <p style={styles.subtitle}>
          {mode === 'register' ? '注册后开启模玩创作之旅' : '登录开启模玩创作之旅'}
        </p>

        {/* 错误提示 */}
        {error && (
          <div style={styles.errorBox}>
            {error}
          </div>
        )}

        {/* 社交登录模式 */}
        {mode === 'social' && (
          <>
            <button 
              onClick={loginWithQQ} 
              style={styles.qqButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(18, 183, 245, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(18, 183, 245, 0.3)';
              }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/Connect_logo_3.png`} 
                alt="QQ登录" 
                style={styles.qqIcon}
              />
              <span>QQ账号登录</span>
            </button>

            <div style={styles.divider}>
              <span style={styles.dividerLine}></span>
              <span style={styles.dividerText}>或</span>
              <span style={styles.dividerLine}></span>
            </div>

            <button 
              onClick={() => setMode('email')} 
              style={styles.emailButton}
            >
              🔐 账号密码登录
            </button>

            <p style={styles.registerHint}>
              还没有账号？
              <button 
                onClick={() => setMode('register')} 
                style={styles.linkButton}
              >
                立即注册
              </button>
            </p>
          </>
        )}

        {/* 账号密码登录模式 */}
        {mode === 'email' && (
          <form onSubmit={handleEmailLogin} style={styles.form}>
            <input
              type="text"
              placeholder="账号 / 邮箱 / 手机号 / 昵称"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              disabled={loading}
            />
            <input
              type="password"
              placeholder="密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              disabled={loading}
            />
            
            <button 
              type="submit" 
              style={styles.submitButton}
              disabled={loading}
            >
              {loading ? '登录中...' : '登录'}
            </button>

            <div style={styles.formLinks}>
              <button 
                type="button"
                onClick={() => { setMode('social'); setError(''); }} 
                style={styles.linkButton}
              >
                ← 返回
              </button>
              <button 
                type="button"
                onClick={() => { setMode('register'); setError(''); }} 
                style={styles.linkButton}
              >
                注册账号
              </button>
            </div>
          </form>
        )}

        {/* 注册模式 */}
        {mode === 'register' && (
          <form onSubmit={handleRegister} style={styles.form}>
            <input
              type="email"
              placeholder="邮箱地址"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              disabled={loading}
            />
            <input
              type="text"
              placeholder="昵称（可选）"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              style={styles.input}
              disabled={loading}
            />
            <input
              type="password"
              placeholder="密码（至少6位）"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              disabled={loading}
            />
            <input
              type="password"
              placeholder="确认密码"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              disabled={loading}
            />
            
            <button 
              type="submit" 
              style={styles.submitButton}
              disabled={loading}
            >
              {loading ? '注册中...' : '注册'}
            </button>

            <div style={styles.formLinks}>
              <button 
                type="button"
                onClick={() => { setMode('social'); setError(''); }} 
                style={styles.linkButton}
              >
                ← 返回
              </button>
              <button 
                type="button"
                onClick={() => { setMode('email'); setError(''); }} 
                style={styles.linkButton}
              >
                已有账号？登录
              </button>
            </div>
          </form>
        )}

        <p style={styles.agreement}>
          登录即表示同意 
          <a href="/terms" style={styles.link}>《用户协议》</a>
          和
          <a href="/privacy" style={styles.link}>《隐私政策》</a>
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '90vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a1d23 0%, #282c34 50%, #1a1d23 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    padding: '40px 36px',
    backgroundColor: 'rgba(37, 40, 48, 0.95)',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    WebkitBackdropFilter: 'blur(20px)',
    backdropFilter: 'blur(20px)',
    textAlign: 'center',
    position: 'relative',
    zIndex: 10,
  },
  logoSection: {
    marginBottom: '20px',
  },
  logo: {
    width: '100px',
    height: 'auto',
    filter: 'drop-shadow(0 4px 20px rgba(255, 255, 255, 0.1))',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '8px',
    letterSpacing: '1px',
  },
  subtitle: {
    fontSize: '14px',
    color: '#9ca3af',
    marginBottom: '24px',
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '10px 16px',
    marginBottom: '16px',
    color: '#ef4444',
    fontSize: '14px',
  },
  qqButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #12B7F5 0%, #0099E5 100%)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(18, 183, 245, 0.3)',
  },
  qqIcon: {
    height: '24px',
    width: 'auto',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
    gap: '12px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  dividerText: {
    color: '#6b7280',
    fontSize: '13px',
  },
  emailButton: {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '12px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '15px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  submitButton: {
    width: '100%',
    padding: '14px 24px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'all 0.3s ease',
  },
  formLinks: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '12px',
  },
  registerHint: {
    marginTop: '20px',
    color: '#6b7280',
    fontSize: '14px',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#12B7F5',
    cursor: 'pointer',
    fontSize: '14px',
    padding: '4px 8px',
  },
  agreement: {
    marginTop: '20px',
    fontSize: '12px',
    color: '#6b7280',
  },
  link: {
    color: '#12B7F5',
    textDecoration: 'none',
  },
};

export default Login;
